import { useState } from 'react';
import {
  Badge,
  Button,
  Center,
  Container,
  Group,
  Loader,
  MultiSelect,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconCheck,
  IconCircleFilled,
  IconPhoneCall,
  IconShieldExclamation,
} from '@tabler/icons-react';
import { createFileRoute } from '@tanstack/react-router';
import { useMutation, useSubscription } from 'urql';
import { CALL_ERROR_TYPE_LABELS, CallErrorDetail } from './-components/call-error-detail';
import { CallDetailModal } from './-components/call-detail-modal';
import {
  CallErrorsSubscription,
  LiveCallsSubscription,
  MarkAllCallErrorsReviewedMutation,
  MarkCallErrorReviewedMutation,
  OrganizationFlowsSubscription,
  OrganizationPhoneNumbersSubscription,
} from './-components/call-graphql';
import { formatElapsed, formatPhone, formatTimeOfDay } from './-components/format';
import { StatTile } from './-components/stat-cards';
import { useNow } from './-components/use-now';
import { useRequireAdmin } from './-components/use-require-admin';
import { useSince } from './-components/use-time-window';

const LIVE_CALL_MAX_AGE_MS = 12 * 60 * 60 * 1000;
const REVIEWED_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;
const ERROR_PAGE_SIZE = 100;

export const Route = createFileRoute('/_authed/active-dashboard')({
  component: ActiveDashboardPage,
});

function idFilter(ids: string[]) {
  return ids.length > 0 ? { _in: ids } : undefined;
}

function ActiveDashboard({ organizationId }: { organizationId: string }) {
  const [flowIds, setFlowIds] = useState<string[]>([]);
  const [phoneNumberIds, setPhoneNumberIds] = useState<string[]>([]);
  const [includeReviewed, setIncludeReviewed] = useState(false);
  const [selectedCallControlId, setSelectedCallControlId] = useState<string | null>(null);
  const now = useNow();
  const liveFloor = useSince(LIVE_CALL_MAX_AGE_MS);
  const reviewedFloor = useSince(REVIEWED_WINDOW_MS);

  const [{ data: flowData }] = useSubscription({
    query: OrganizationFlowsSubscription,
    variables: { organizationId },
  });
  const [{ data: phoneData }] = useSubscription({
    query: OrganizationPhoneNumbersSubscription,
    variables: { organizationId },
  });

  const [{ data: liveData }] = useSubscription({
    query: LiveCallsSubscription,
    variables: {
      where: {
        organizationId: { _eq: organizationId },
        status: { _eq: 'active' },
        startedAt: { _gte: liveFloor },
        flowId: idFilter(flowIds),
        phoneNumberId: idFilter(phoneNumberIds),
      },
    },
  });

  const [{ data: errorData }] = useSubscription({
    query: CallErrorsSubscription,
    variables: {
      limit: ERROR_PAGE_SIZE,
      where: {
        organizationId: { _eq: organizationId },
        flowId: idFilter(flowIds),
        phoneNumberId: idFilter(phoneNumberIds),
        ...(includeReviewed ? { occurredAt: { _gte: reviewedFloor } } : { reviewedAt: { _isNull: true } }),
      },
    },
  });

  const [, markReviewed] = useMutation(MarkCallErrorReviewedMutation);
  const [, markAllReviewed] = useMutation(MarkAllCallErrorsReviewedMutation);

  const liveCalls = liveData?.callSessions;
  const errors = errorData?.callErrors;
  const unreviewedCount = (errors ?? []).filter((e) => !e.reviewedAt).length;
  const fatalCount = (errors ?? []).filter((e) => !e.reviewedAt && e.severity === 'fatal').length;

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        <div>
          <Title order={1}>Active Dashboard</Title>
          <Text c="dimmed" size="sm">
            Live calls and platform errors, in real time
          </Text>
        </div>

        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
          <StatTile
            label="Live Calls"
            value={liveCalls?.length ?? '—'}
            icon={<IconPhoneCall size={24} />}
            color={(liveCalls?.length ?? 0) > 0 ? 'green' : 'gray'}
          />
          <StatTile
            label="Unreviewed Errors"
            value={errors === undefined ? '—' : unreviewedCount}
            icon={<IconAlertTriangle size={24} />}
            color={unreviewedCount > 0 ? 'orange' : 'gray'}
          />
          <StatTile
            label="Unreviewed Fatal"
            value={errors === undefined ? '—' : fatalCount}
            icon={<IconShieldExclamation size={24} />}
            color={fatalCount > 0 ? 'red' : 'gray'}
          />
        </SimpleGrid>

        <Paper p="md" radius="lg" withBorder>
          <Group align="flex-end" gap="md">
            <MultiSelect
              label="Phone numbers"
              placeholder={phoneNumberIds.length === 0 ? 'All numbers' : undefined}
              data={(phoneData?.phoneNumbers ?? []).map((p) => ({
                value: p.id,
                label: p.friendlyName ?? formatPhone(p.phoneNumber),
              }))}
              value={phoneNumberIds}
              onChange={setPhoneNumberIds}
              clearable
              searchable
              flex={1}
            />
            <MultiSelect
              label="Flows"
              placeholder={flowIds.length === 0 ? 'All flows' : undefined}
              data={(flowData?.flows ?? []).map((f) => ({ value: f.id, label: f.name }))}
              value={flowIds}
              onChange={setFlowIds}
              clearable
              searchable
              flex={1}
            />
          </Group>
        </Paper>

        <div>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <IconCircleFilled size={10} color="var(--mantine-color-green-6)" />
              <Text fw={600}>Live Calls</Text>
            </Group>
          </Group>
          {liveCalls === undefined ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : liveCalls.length === 0 ? (
            <Paper p="xl" radius="lg" withBorder>
              <Text c="dimmed" size="sm" ta="center">
                No live calls right now.
              </Text>
            </Paper>
          ) : (
            <Paper radius="lg" withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>From</Table.Th>
                    <Table.Th>To</Table.Th>
                    <Table.Th>Flow</Table.Th>
                    <Table.Th>Direction</Table.Th>
                    <Table.Th>Elapsed</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {liveCalls.map((call) => (
                    <Table.Tr
                      key={call.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() => setSelectedCallControlId(call.callControlId)}
                    >
                      <Table.Td>{formatPhone(call.fromNumber)}</Table.Td>
                      <Table.Td>
                        {call.phoneNumber?.friendlyName ?? formatPhone(call.phoneNumber?.phoneNumber ?? call.toNumber)}
                      </Table.Td>
                      <Table.Td>{call.flow?.name ?? '—'}</Table.Td>
                      <Table.Td>
                        <Badge size="sm" variant="light" color={call.direction === 'inbound' ? 'blue' : 'grape'}>
                          {call.direction}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{formatElapsed(call.startedAt, now)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </div>

        <div>
          <Group justify="space-between" mb="md">
            <Group gap="xs">
              <IconAlertTriangle size={16} color="var(--mantine-color-orange-6)" />
              <Text fw={600}>Errored Calls</Text>
            </Group>
            <Group gap="md">
              <Switch
                label="Show reviewed"
                checked={includeReviewed}
                onChange={(e) => setIncludeReviewed(e.currentTarget.checked)}
                size="sm"
              />
              {unreviewedCount > 0 && (
                <Button
                  size="xs"
                  variant="light"
                  leftSection={<IconCheck size={14} />}
                  onClick={() => void markAllReviewed({ organizationId, reviewedAt: new Date().toISOString() })}
                >
                  Mark all reviewed
                </Button>
              )}
            </Group>
          </Group>
          {errors === undefined ? (
            <Center py="xl">
              <Loader />
            </Center>
          ) : errors.length === 0 ? (
            <Paper p="xl" radius="lg" withBorder>
              <Text c="dimmed" size="sm" ta="center">
                {includeReviewed ? 'No errors in the recent window.' : 'No unreviewed errors.'}
              </Text>
            </Paper>
          ) : (
            <Paper radius="lg" withBorder>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Severity</Table.Th>
                    <Table.Th>Type</Table.Th>
                    <Table.Th>Message</Table.Th>
                    <Table.Th>From</Table.Th>
                    <Table.Th>Flow</Table.Th>
                    <Table.Th>When</Table.Th>
                    <Table.Th />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {errors.map((err) => (
                    <Table.Tr
                      key={err.id}
                      style={{ cursor: 'pointer', opacity: err.reviewedAt ? 0.55 : 1 }}
                      onClick={() => setSelectedCallControlId(err.callControlId)}
                    >
                      <Table.Td>
                        <Badge size="sm" color={err.severity === 'fatal' ? 'red' : 'yellow'} variant="filled">
                          {err.severity}
                        </Badge>
                      </Table.Td>
                      <Table.Td>{CALL_ERROR_TYPE_LABELS[err.errorType] ?? err.errorType}</Table.Td>
                      <Table.Td>
                        <Text size="sm" lineClamp={2} maw={360}>
                          {err.message}
                        </Text>
                        {err.nodeName && (
                          <Text size="xs" c="dimmed">
                            {err.nodeName}
                          </Text>
                        )}
                        <CallErrorDetail detail={err.details} />
                      </Table.Td>
                      <Table.Td>{formatPhone(err.fromNumber)}</Table.Td>
                      <Table.Td>{err.flow?.name ?? '—'}</Table.Td>
                      <Table.Td>{formatTimeOfDay(err.occurredAt)}</Table.Td>
                      <Table.Td onClick={(e) => e.stopPropagation()}>
                        {err.reviewedAt ? (
                          <Badge size="xs" variant="light" color="gray">
                            reviewed
                          </Badge>
                        ) : (
                          <Tooltip label="Mark reviewed">
                            <Button
                              size="compact-xs"
                              variant="subtle"
                              onClick={() => void markReviewed({ id: err.id, reviewedAt: new Date().toISOString() })}
                            >
                              <IconCheck size={14} />
                            </Button>
                          </Tooltip>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </div>
      </Stack>

      <CallDetailModal
        organizationId={organizationId}
        callControlId={selectedCallControlId}
        onClose={() => setSelectedCallControlId(null)}
      />
    </Container>
  );
}

function ActiveDashboardPage() {
  const { organizationId, isAdmin, isLoading } = useRequireAdmin();

  if (isLoading) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!organizationId || !isAdmin) {
    return (
      <Center h="50vh">
        <Text c="dimmed">No organization selected.</Text>
      </Center>
    );
  }

  return <ActiveDashboard organizationId={organizationId} />;
}
